/**
 * components/landing/home/architecture/data.js — Phase 2.2 · single source
 * of truth for the Interactive Platform Architecture engine.
 *
 * Every module, its metadata, its relationships, and every view mode
 * lives here so the visualisation can be re-configured without any
 * structural code change.  Add a new module = add one entry here.
 *
 * The renderer projects modules onto two concentric rings by their
 * `ring` property and evenly spaces them by `slot` around the ring.
 * Angles are computed at render time from slot indices, so re-ordering
 * this file naturally re-arranges the wheel.
 *
 * ── Module schema ──
 *   id              stable slug (used in URLs / connection edges)
 *   name            display label
 *   ring            'inner' | 'outer'
 *   slot            integer position within its ring (0 → CW around)
 *   tone            'orange' | 'green' | 'blue' | 'purple' | 'cyan'
 *   category        used by view modes to filter/highlight
 *   iconKey         lucide-react icon identifier (resolved in Canvas)
 *   purpose         one-line tagline
 *   overview        2–3 sentence marketing description
 *   businessValue   why it matters commercially
 *   features        3–5 bullet points
 *   benefits        3 business-benefit bullets
 *   connections     ids of directly-connected modules (undirected)
 *   cta             { label, href }
 *   preview         key for the right-panel preview registry
 */

// ─── Modules ────────────────────────────────────────────────────────────
export const MODULES = [
  // ── INNER RING (9 · primary product modules) ─────────────────────────
  {
    id: 'ai-mentor',
    name: 'AI Mentor',
    ring: 'inner', slot: 0, tone: 'orange',
    category: 'ai', iconKey: 'Bot',
    purpose: 'Contextual AI guidance across every workflow.',
    overview: 'A production-grade RAG assistant grounded in your own strategies, docs, and market data. Answers questions in-context and can act on the workspace.',
    businessValue: 'Turns every operator into a senior analyst — cuts ramp-up from months to days.',
    features: ['RAG over your KB', 'Strategy explainer', 'Chart-aware answers', 'Function-calling actions', 'Role-scoped knowledge'],
    benefits: ['Reduce training cost 60%', 'Faster decisions under load', 'Compliant, auditable AI'],
    connections: ['knowledge-base', 'strategy-builder', 'analytics', 'automation', 'scanner', 'trading-academy'],
    cta: { label: 'Explore AI Mentor', href: '/ai' },
    preview: 'ai',
  },
  {
    id: 'trading-terminal',
    name: 'Trading Terminal',
    ring: 'inner', slot: 1, tone: 'green',
    category: 'trading', iconKey: 'LineChart',
    purpose: 'Institutional-grade execution surface.',
    overview: 'Multi-asset order routing, hotkeys, ladder DOM, bracket & OCO orders — everything a pro desk needs, in the browser.',
    businessValue: 'One terminal replaces four legacy tools; higher fill quality & lower slippage.',
    features: ['Ladder / DOM', 'Hotkeys & workspaces', 'Bracket & OCO', 'Multi-broker adapters', 'Latency HUD'],
    benefits: ['Faster execution', 'Fewer fat-finger errors', 'Reduced infra spend'],
    connections: ['portfolio', 'scanner', 'market-viewer', 'backtesting', 'automation', 'api-gateway'],
    cta: { label: 'Open Terminal Tour', href: '/trading' },
    preview: 'terminal',
  },
  {
    id: 'strategy-builder',
    name: 'Strategy Builder',
    ring: 'inner', slot: 2, tone: 'purple',
    category: 'trading', iconKey: 'Wand2',
    purpose: 'Visual, no-code strategy authoring.',
    overview: 'Drag-and-drop blocks for signals, filters, risk, and exits. Compiles to a testable, deployable strategy artefact.',
    businessValue: 'Analysts ship strategies without depending on engineering.',
    features: ['Node canvas', 'Signal library', 'Risk blocks', 'Version history', 'One-click backtest'],
    benefits: ['10× faster iteration', 'No engineer bottleneck', 'Reusable playbooks'],
    connections: ['ai-mentor', 'backtesting', 'automation', 'analytics'],
    cta: { label: 'See Strategy Builder', href: '/features' },
    preview: 'strategy',
  },
  {
    id: 'backtesting',
    name: 'Backtesting',
    ring: 'inner', slot: 3, tone: 'blue',
    category: 'trading', iconKey: 'FlaskConical',
    purpose: 'Historical validation with realistic assumptions.',
    overview: 'Tick-accurate simulator with slippage, commissions, walk-forward optimisation, and Monte-Carlo stress runs.',
    businessValue: 'Deploys survive real markets — not just curve fits.',
    features: ['Tick precision', 'Walk-forward', 'Monte-Carlo', 'Regime slicing', 'Report artefacts'],
    benefits: ['Confidence to deploy', 'Stakeholder-ready reports', 'Fewer drawdowns'],
    connections: ['strategy-builder', 'analytics', 'portfolio', 'knowledge-base'],
    cta: { label: 'View Backtest Sample', href: '/features' },
    preview: 'backtest',
  },
  {
    id: 'portfolio',
    name: 'Portfolio',
    ring: 'inner', slot: 4, tone: 'green',
    category: 'trading', iconKey: 'Wallet',
    purpose: 'Positions, exposure & risk in one pane.',
    overview: 'Live portfolio view with greeks, factor exposure, VaR, and drawdown alarms.',
    businessValue: 'Risk you can see is risk you can manage.',
    features: ['Live P&L', 'Factor exposure', 'VaR / drawdown', 'Multi-currency', 'Broker aggregation'],
    benefits: ['Real-time risk clarity', 'Regulator-ready views', 'Faster hedging'],
    connections: ['trading-terminal', 'analytics', 'notifications', 'billing'],
    cta: { label: 'Portfolio Preview', href: '/features' },
    preview: 'portfolio',
  },
  {
    id: 'market-viewer',
    name: 'Market Viewer',
    ring: 'inner', slot: 5, tone: 'cyan',
    category: 'trading', iconKey: 'BarChart3',
    purpose: 'Multi-timeframe market lens.',
    overview: 'Sync-linked charts, order-flow footprint, and multi-market watchlists in one view.',
    businessValue: 'One glance replaces switching between five tools.',
    features: ['Sync-linked charts', 'Footprint / delta', 'Watchlists', 'Custom indicators', 'Alerts'],
    benefits: ['Faster context', 'Less tool sprawl', 'Better entries'],
    connections: ['scanner', 'trading-terminal', 'analytics'],
    cta: { label: 'Open Market Viewer', href: '/features' },
    preview: 'marketviewer',
  },
  {
    id: 'scanner',
    name: 'Scanner',
    ring: 'inner', slot: 6, tone: 'orange',
    category: 'trading', iconKey: 'ScanLine',
    purpose: 'Systematic opportunity detection.',
    overview: 'Real-time scans across thousands of instruments with custom scoring, priority queues, and one-click send-to-terminal.',
    businessValue: 'Never miss a setup — even overnight.',
    features: ['Real-time engine', 'Custom scoring', 'Priority queues', 'Send-to-terminal', 'Alert routing'],
    benefits: ['More qualified ideas', 'Coverage 24/5', 'Priority focus'],
    connections: ['ai-mentor', 'market-viewer', 'trading-terminal', 'notifications'],
    cta: { label: 'Scanner Overview', href: '/features' },
    preview: 'scanner',
  },
  {
    id: 'trading-academy',
    name: 'Trading Academy',
    ring: 'inner', slot: 7, tone: 'purple',
    category: 'ai', iconKey: 'GraduationCap',
    purpose: 'Programmatic operator upskilling.',
    overview: 'Structured learning paths with quizzes, cohort tracking, and AI-adaptive lessons.',
    businessValue: 'A trained desk performs — the Academy trains the desk.',
    features: ['Learning paths', 'Cohort progress', 'Quizzes & practicals', 'Adaptive lessons', 'Certification'],
    benefits: ['Consistent skill floor', 'Retention & engagement', 'Faster onboarding'],
    connections: ['ai-mentor', 'analytics', 'knowledge-base'],
    cta: { label: 'Academy Overview', href: '/features' },
    preview: 'academy',
  },
  {
    id: 'analytics',
    name: 'Analytics',
    ring: 'inner', slot: 8, tone: 'blue',
    category: 'ai', iconKey: 'PieChart',
    purpose: 'Operator, portfolio and business intelligence.',
    overview: 'One BI layer that spans traders, portfolios and business metrics — no ETL, no external warehouse.',
    businessValue: 'Executives get a single truth pane, in real time.',
    features: ['Live dashboards', 'Cross-module joins', 'Cohort views', 'Custom KPIs', 'CSV / API export'],
    benefits: ['Faster decisions', 'Zero-ETL BI', 'C-suite visibility'],
    connections: ['portfolio', 'backtesting', 'trading-academy', 'crm', 'billing', 'automation'],
    cta: { label: 'Analytics Tour', href: '/features' },
    preview: 'analytics',
  },
  // ── OUTER RING (9 · platform + business ops) ─────────────────────────
  {
    id: 'knowledge-base',
    name: 'Knowledge Base',
    ring: 'outer', slot: 0, tone: 'orange',
    category: 'ai', iconKey: 'BookOpen',
    purpose: 'Enterprise RAG index of your knowledge.',
    overview: 'Ingests PDFs, decks, sheets, notes and code into a vector index scoped by role and tenant.',
    businessValue: 'Your institutional memory becomes queryable IP.',
    features: ['Chunked embeddings', 'Role scoping', 'Freshness policies', 'Auditable citations', 'Multi-tenant'],
    benefits: ['Retain institutional IP', 'Compliant AI', 'Faster expert answers'],
    connections: ['ai-mentor', 'trading-academy', 'security', 'api-gateway'],
    cta: { label: 'Knowledge Base', href: '/ai' },
    preview: 'kb',
  },
  {
    id: 'automation',
    name: 'Automation',
    ring: 'outer', slot: 1, tone: 'purple',
    category: 'ai', iconKey: 'Workflow',
    purpose: 'Deterministic + AI workflow engine.',
    overview: 'Triggers, guards, and actions across modules — human-in-the-loop where you need it, autonomous where you trust it.',
    businessValue: 'Scale operator throughput without scaling headcount.',
    features: ['Event triggers', 'Approval gates', 'AI-assisted steps', 'Retry & audit', 'Cross-module actions'],
    benefits: ['Ops scale linearly', 'Fewer manual errors', 'Nights & weekends covered'],
    connections: ['ai-mentor', 'strategy-builder', 'trading-terminal', 'billing', 'notifications', 'analytics'],
    cta: { label: 'Automation Playbook', href: '/features' },
    preview: 'automation',
  },
  {
    id: 'billing',
    name: 'Billing',
    ring: 'outer', slot: 2, tone: 'green',
    category: 'ops', iconKey: 'CreditCard',
    purpose: 'Subscriptions, metering & revenue.',
    overview: 'Stripe-grade billing with plans, seats, metered usage, prorations, dunning and revenue reporting.',
    businessValue: 'Every white-label instance monetises on day one.',
    features: ['Plans & seats', 'Metered usage', 'Proration & dunning', 'Tax & invoices', 'Multi-currency'],
    benefits: ['Faster time-to-revenue', 'Lower revenue leakage', 'Automated collections'],
    connections: ['white-label', 'crm', 'affiliate', 'automation', 'portfolio'],
    cta: { label: 'Billing Overview', href: '/pricing' },
    preview: 'billing',
  },
  {
    id: 'crm',
    name: 'CRM',
    ring: 'outer', slot: 3, tone: 'blue',
    category: 'ops', iconKey: 'UsersRound',
    purpose: 'Customer lifecycle at platform scale.',
    overview: 'Leads, accounts, contacts, tickets and lifecycle automations — first-class citizen of the platform.',
    businessValue: 'One brain across sales, support and success.',
    features: ['Lead + account model', 'Ticketing', 'Lifecycle stages', 'Automations', 'API + webhooks'],
    benefits: ['Higher LTV', 'Better retention', 'Unified customer story'],
    connections: ['billing', 'notifications', 'white-label', 'affiliate', 'analytics'],
    cta: { label: 'CRM Overview', href: '/features' },
    preview: 'crm',
  },
  {
    id: 'affiliate',
    name: 'Affiliate',
    ring: 'outer', slot: 4, tone: 'orange',
    category: 'ops', iconKey: 'HandCoins',
    purpose: 'Partner-led growth engine.',
    overview: 'Referral tracking, tiered payouts, partner dashboards and marketing collateral — plug-and-play.',
    businessValue: 'Turn users into a distribution channel.',
    features: ['Referral tracking', 'Tiered payouts', 'Partner dashboards', 'Collateral hub', 'Attribution'],
    benefits: ['Scalable acquisition', 'Lower CAC', 'Community-led growth'],
    connections: ['crm', 'billing', 'notifications', 'analytics'],
    cta: { label: 'Affiliate Program', href: '/features' },
    preview: 'affiliate',
  },
  {
    id: 'white-label',
    name: 'White-Label',
    ring: 'outer', slot: 5, tone: 'purple',
    category: 'wl', iconKey: 'Building2',
    purpose: 'Deploy your own branded platform.',
    overview: 'Multi-tenant isolation, custom domains, branding, licensing and admin — spin up a full SaaS in hours.',
    businessValue: 'Your brand, our engine. Ship a market-ready product this quarter.',
    features: ['Multi-tenant isolation', 'Custom domains', 'Full theming', 'Licensing & seats', 'Tenant admin'],
    benefits: ['Own the customer', 'New revenue line', 'Faster time-to-market'],
    connections: ['billing', 'crm', 'security', 'api-gateway', 'notifications', 'ai-mentor'],
    cta: { label: 'White-Label Overview', href: '/white-label' },
    preview: 'whitelabel',
  },
  {
    id: 'api-gateway',
    name: 'API Gateway',
    ring: 'outer', slot: 6, tone: 'cyan',
    category: 'infra', iconKey: 'Network',
    purpose: 'One gateway to every integration.',
    overview: 'REST + WebSocket surface for brokers, data vendors, custom apps and partner ecosystems — with typed SDKs.',
    businessValue: 'Never rebuild an integration twice.',
    features: ['REST + WS', 'Typed SDKs', 'OAuth2 & keys', 'Rate limits', 'Version pinning'],
    benefits: ['Faster partner onboarding', 'Reusable connectors', 'Ecosystem leverage'],
    connections: ['trading-terminal', 'security', 'knowledge-base', 'white-label'],
    cta: { label: 'API Docs', href: '/docs' },
    preview: 'api',
  },
  {
    id: 'security',
    name: 'Security',
    ring: 'outer', slot: 7, tone: 'green',
    category: 'infra', iconKey: 'ShieldCheck',
    purpose: 'Identity, permissions & compliance.',
    overview: 'SSO, RBAC, audit logs, encryption at-rest and in-transit — enterprise-ready by default.',
    businessValue: 'Pass procurement in weeks, not quarters.',
    features: ['SSO + MFA', 'RBAC & ABAC', 'Audit logs', 'Encryption', 'SOC-2 pathways'],
    benefits: ['Enterprise-ready', 'Reduced breach risk', 'Auditor-friendly'],
    connections: ['api-gateway', 'knowledge-base', 'white-label'],
    cta: { label: 'Security Overview', href: '/docs' },
    preview: 'security',
  },
  {
    id: 'notifications',
    name: 'Notifications',
    ring: 'outer', slot: 8, tone: 'orange',
    category: 'infra', iconKey: 'BellRing',
    purpose: 'Cross-channel communication engine.',
    overview: 'Email, SMS, push and in-app — templated, versioned and delivery-tracked, from a single API.',
    businessValue: 'Reach the right operator, on the right channel, at the right moment.',
    features: ['Multi-channel', 'Template registry', 'Delivery tracking', 'Rate control', 'Localisation'],
    benefits: ['Higher engagement', 'Fewer missed alerts', 'One vendor to manage'],
    connections: ['portfolio', 'scanner', 'automation', 'crm', 'affiliate', 'white-label'],
    cta: { label: 'Notifications', href: '/features' },
    preview: 'notifications',
  },
]

// ─── View modes ─────────────────────────────────────────────────────────
// `highlight` is either 'all' or an array of module ids.  Modules NOT in
// the highlight set are dimmed but still visible for context.
export const VIEW_MODES = [
  {
    id: 'overview',
    label: 'Platform Overview',
    short: 'Overview',
    tone: 'orange',
    description: 'All modules working in harmony.',
    highlight: 'all',
  },
  {
    id: 'ai',
    label: 'AI Intelligence',
    short: 'AI',
    tone: 'purple',
    description: 'Where AI compounds across the platform.',
    highlight: ['ai-mentor', 'knowledge-base', 'automation', 'analytics', 'trading-academy'],
  },
  {
    id: 'trading',
    label: 'Trading Infrastructure',
    short: 'Trading',
    tone: 'green',
    description: 'The execution stack, end to end.',
    highlight: ['trading-terminal', 'strategy-builder', 'scanner', 'portfolio', 'backtesting', 'market-viewer'],
  },
  {
    id: 'ops',
    label: 'Business Operations',
    short: 'Ops',
    tone: 'blue',
    description: 'CRM, billing, and revenue engines.',
    highlight: ['crm', 'billing', 'affiliate', 'notifications', 'analytics'],
  },
  {
    id: 'wl',
    label: 'White-Label',
    short: 'White-Label',
    tone: 'cyan',
    description: 'Your brand, our platform — end to end.',
    highlight: ['white-label', 'billing', 'crm', 'security', 'api-gateway', 'notifications', 'ai-mentor'],
  },
]

// ─── Helpers ────────────────────────────────────────────────────────────
export const getModule = (id) => MODULES.find((m) => m.id === id)

/**
 * Return the set of module ids that are "related" to `id` — the module
 * itself plus everything it declares in `connections` (directed) plus
 * anything that declares HIM in its connections (reverse).  Used to
 * highlight neighbours on hover.
 */
export const relatedIds = (id) => {
  const seed = getModule(id)
  if (!seed) return new Set([id])
  const set = new Set([id, ...(seed.connections || [])])
  for (const m of MODULES) {
    if ((m.connections || []).includes(id)) set.add(m.id)
  }
  return set
}

/**
 * Build the undirected edge set of the platform — used by the SVG
 * canvas to draw connection lines.  Each edge is a tuple [aId, bId]
 * and no pair is repeated.
 */
export const buildEdges = () => {
  const seen = new Set()
  const edges = []
  for (const m of MODULES) {
    for (const other of m.connections || []) {
      const key = [m.id, other].sort().join('::')
      if (seen.has(key)) continue
      seen.add(key)
      edges.push([m.id, other])
    }
  }
  return edges
}

/**
 * Tone → hex + rgb helper used across SVG (need raw values because
 * SVG stops don't accept Tailwind classes).
 */
export const TONE_COLORS = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

// Ring geometry — expressed in SVG viewBox units (0..1000, centered
// on 500,500).  Renderer multiplies these by viewBox scale.
export const GEOMETRY = {
  viewBox: 1000,
  center: 500,
  coreRadius: 62,
  innerRingRadius: 210,
  outerRingRadius: 390,
  nodeRadiusInner: 34,
  nodeRadiusOuter: 30,
}
